import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Store, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Star, 
  Users, 
  Package, 
  TrendingUp,
  MessageSquare,
  Heart,
  Filter,
  ChevronRight,
  Clock,
  Shield,
  CheckCircle,
  Image as ImageIcon,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Edit,
  Settings,
  ShoppingBag,
  Briefcase,
  Award,
  BarChart3,
  Camera,
  Share2,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import useMarketplaceStore from '../../store/marketplaceStore';
import useAuthStore from '../../store/authStore';
import MessageBusinessModal from '../../components/community/MessageBusinessModal';
import ReviewModal from '../../components/community/ReviewModal';
import OrderModal from '../../components/community/OrderModal';
import ProductModal from '../../components/community/ProductModal';
import ServiceModal from '../../components/community/ServiceModal';
import EditBusinessModal from '../../components/community/EditBusinessModal';
import CategoryTabs from '../../components/community/CategoryTabs';
import ProductServiceCard from '../../components/community/ProductServiceCard';
import QuickViewModal from '../../components/community/QuickViewModal';
import marketplaceAPI from '../../services/marketplace.api';
import { BUSINESS_CATEGORIES, PRODUCT_SERVICE_CATEGORIES } from '../../config/categories';

const BusinessProfilePage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [showEditBusinessModal, setShowEditBusinessModal] = useState(false);
  const [reviewsFilter, setReviewsFilter] = useState('all');
  const [isSaved, setIsSaved] = useState(false);
  
  const [showProductModal, setShowProductModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedItemForOrder, setSelectedItemForOrder] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);

  const [activeCategory, setActiveCategory] = useState('ALL');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('recommended');
  const [searchQuery, setSearchQuery] = useState('');
  const [quickViewItem, setQuickViewItem] = useState(null);
  const [quickViewType, setQuickViewType] = useState(null);
  const [filteredItems, setFilteredItems] = useState({ products: [], services: [] });
  const [isFiltering, setIsFiltering] = useState(false);

  const { 
    getBusinessDetails,
    getReviews,
    selectedBusiness,
    reviews,
    reviewsSummary,
    isLoading
  } = useMarketplaceStore();

  const { user } = useAuthStore();
  
  const isOwner = user?.id === selectedBusiness?.userId;

  const getBusinessCategoryInfo = (categoryId) => {
    return BUSINESS_CATEGORIES.find(c => c.id === categoryId) || { 
      name: categoryId || 'Business', 
      icon: '🏢', 
      color: 'bg-primary-100 text-primary-700'
    };
  };

  const getProductCategoryInfo = (categoryId) => {
    const category = PRODUCT_SERVICE_CATEGORIES[categoryId];
    return category || { 
      name: categoryId || 'Product', 
      icon: '📦',
      color: 'bg-gray-100 text-gray-700'
    };
  };

  useEffect(() => {
    loadBusinessData();
    checkIfSaved();
  }, [businessId]);

  useEffect(() => {
    if (selectedBusiness && isOwner) {
      fetchPendingOrdersCount();
    }
  }, [selectedBusiness, isOwner]);

  const loadBusinessData = async () => {
    setIsInitialLoading(true);
    try {
      await Promise.all([
        getBusinessDetails(businessId),
        getReviews({ businessId })
      ]);
      await new Promise(resolve => setTimeout(resolve, 50));
    } catch (error) {
      console.error('Failed to load business data:', error);
      toast.error('Failed to load business profile');
      navigate('/community/marketplace');
    } finally {
      setIsInitialLoading(false);
    }
  };

  const checkIfSaved = async () => {
    try {
      const response = await marketplaceAPI.checkFavorite(businessId);
      setIsSaved(response.data?.isFavorite || false);
    } catch (error) {
      console.error('Failed to check favorite status:', error);
    }
  };

  const fetchPendingOrdersCount = async () => {
    if (!isOwner || !businessId) return;
    
    setIsLoadingOrders(true);
    try {
      const response = await marketplaceAPI.getBusinessOrders(businessId, { status: 'PENDING' });
      
      let orders = [];
      if (response.data?.orders) {
        orders = response.data.orders;
      } else if (response.orders) {
        orders = response.orders;
      }
      
      setPendingOrdersCount(orders.length);
    } catch (error) {
      console.error('Failed to fetch pending orders:', error);
      setPendingOrdersCount(0);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.share({
        title: selectedBusiness?.name,
        text: `Check out ${selectedBusiness?.name} on Fastel Marketplace`,
        url
      });
    } catch (error) {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleSaveBusiness = async () => {
    try {
      const result = await marketplaceAPI.toggleFavorite(businessId);
      setIsSaved(!isSaved);
      toast.success(result.message);
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to save business');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await marketplaceAPI.deleteProduct(productId);
      toast.success('Product deleted successfully');
      loadBusinessData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await marketplaceAPI.deleteService(serviceId);
      toast.success('Service deleted successfully');
      loadBusinessData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const getAvailableCategories = () => {
    if (!selectedBusiness) return [];
    
    const categoryMap = new Map();
    
    (selectedBusiness.products || []).forEach(product => {
      if (product.status === 'ACTIVE' && !product.deletedAt) {
        const cat = product.category;
        const categoryInfo = getProductCategoryInfo(cat);
        if (!categoryMap.has(cat)) {
          categoryMap.set(cat, { 
            name: cat, 
            count: 0, 
            displayName: categoryInfo.name,
            shortName: categoryInfo.name.slice(0, 10),
            icon: categoryInfo.icon
          });
        }
        categoryMap.get(cat).count++;
      }
    });
    
    (selectedBusiness.services || []).forEach(service => {
      if (service.status === 'ACTIVE' && !service.deletedAt) {
        const cat = service.category;
        const categoryInfo = getProductCategoryInfo(cat);
        if (!categoryMap.has(cat)) {
          categoryMap.set(cat, { 
            name: cat, 
            count: 0, 
            displayName: categoryInfo.name,
            shortName: categoryInfo.name.slice(0, 10),
            icon: categoryInfo.icon
          });
        }
        categoryMap.get(cat).count++;
      }
    });
    
    const categories = Array.from(categoryMap.values());
    categories.sort((a, b) => b.count - a.count);
    
    return categories;
  };

  const filterAndSortItems = (products, services, category, sort, query) => {
    let filteredProducts = [...products];
    let filteredServices = [...services];
    
    if (category !== 'ALL') {
      filteredProducts = filteredProducts.filter(p => p.category === category);
      filteredServices = filteredServices.filter(s => s.category === category);
    }
    
    if (query.trim()) {
      const lowerQuery = query.toLowerCase();
      filteredProducts = filteredProducts.filter(p => 
        p.name.toLowerCase().includes(lowerQuery) || 
        p.description?.toLowerCase().includes(lowerQuery)
      );
      filteredServices = filteredServices.filter(s => 
        s.title.toLowerCase().includes(lowerQuery) || 
        s.description?.toLowerCase().includes(lowerQuery)
      );
    }
    
    const sortItems = (items, isProduct) => {
      switch (sort) {
        case 'price_asc':
          return [...items].sort((a, b) => (isProduct ? a.price : a.basePrice) - (isProduct ? b.price : b.basePrice));
        case 'price_desc':
          return [...items].sort((a, b) => (isProduct ? b.price : b.basePrice) - (isProduct ? a.price : a.basePrice));
        case 'newest':
          return [...items].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        case 'popular':
          return [...items].sort((a, b) => (b.salesCount || b.completedOrders || 0) - (a.salesCount || a.completedOrders || 0));
        case 'rating':
          return [...items].sort((a, b) => (b.rating || 0) - (a.rating || 0));
        case 'recommended':
        default:
          return [...items].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
      }
    };
    
    return {
      products: sortItems(filteredProducts, true),
      services: sortItems(filteredServices, false)
    };
  };

  useEffect(() => {
    if (selectedBusiness) {
      setIsFiltering(true);
      setTimeout(() => {
        const filtered = filterAndSortItems(
          selectedBusiness.products || [],
          selectedBusiness.services || [],
          activeCategory,
          sortBy,
          searchQuery
        );
        setFilteredItems(filtered);
        setIsFiltering(false);
      }, 100);
    }
  }, [selectedBusiness, activeCategory, sortBy, searchQuery]);

  const getAllItemsCount = () => {
    return (filteredItems.products.length + filteredItems.services.length);
  };

  const handleQuickView = (item, type) => {
    setQuickViewItem(item);
    setQuickViewType(type);
  };

  const handleOrderFromQuickView = (item, type) => {
    setQuickViewItem(null);
    setQuickViewType(null);
    if (type === 'product') {
      setSelectedItemForOrder(item);
      setSelectedItemType('product');
    } else {
      setSelectedItemForOrder(item);
      setSelectedItemType('service');
    }
    setShowOrderModal(true);
  };

  const handleInquireFromQuickView = (item, type) => {
    setQuickViewItem(null);
    setQuickViewType(null);
    if (type === 'product') {
      setSelectedProduct(item);
    } else {
      setSelectedService(item);
    }
    setShowMessageModal(true);
  };

  const toggleFavorite = async (itemId) => {
    try {
      const result = await marketplaceAPI.toggleFavorite(businessId);
      setIsSaved(!isSaved);
      return result;
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      throw error;
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-2 w-2 sm:h-3 sm:w-3 ${star <= Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const getRatingPercentage = (count) => {
    if (!reviewsSummary?.totalReviews) return 0;
    return ((count / reviewsSummary.totalReviews) * 100).toFixed(0);
  };

  const filteredReviews = reviews?.filter(review => {
    if (reviewsFilter === 'all') return true;
    if (reviewsFilter === '5') return review.rating === 5;
    if (reviewsFilter === '4') return review.rating === 4;
    if (reviewsFilter === '3') return review.rating === 3;
    if (reviewsFilter === '2') return review.rating === 2;
    if (reviewsFilter === '1') return review.rating === 1;
    return true;
  });

  const tabs = [
    { id: 'overview', name: 'Overview', short: 'Over', icon: Store },
    { id: 'products', name: 'Products', short: 'Prod', icon: Package },
    { id: 'services', name: 'Services', short: 'Serv', icon: TrendingUp },
    { id: 'reviews', name: 'Reviews', short: 'Rev', icon: Star },
    { id: 'about', name: 'About', short: 'About', icon: Users }
  ];

  const businessCategoryInfo = selectedBusiness?.category ? getBusinessCategoryInfo(selectedBusiness.category) : null;

  if (isInitialLoading || (isLoading && !selectedBusiness)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isInitialLoading && !selectedBusiness) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <div className="inline-flex items-center justify-center w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-red-50 shadow-neomorph-inset mb-3 sm:mb-4">
          <Store className="h-8 w-8 sm:h-12 sm:w-12 text-red-400" />
        </div>
        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">Business Not Found</h3>
        <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">The business you're looking for doesn't exist or has been removed.</p>
        <button
          onClick={() => navigate('/community/marketplace')}
          className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-sm"
        >
          Browse Marketplace
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-4 pb-12 sm:pb-0">
      {/* Cover Image - Reduced height */}
      <div className="relative h-32 sm:h-40 md:h-48 rounded-xl sm:rounded-2xl overflow-hidden shadow-neomorph border border-primary-100/30 mx-3 sm:mx-0">
        {selectedBusiness.coverImageUrl ? (
          <img
            src={selectedBusiness.coverImageUrl}
            alt="Cover"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-primary-600 to-primary-800 flex items-center justify-center">
            <Store className="h-8 w-8 sm:h-12 sm:w-12 text-white opacity-50" />
          </div>
        )}
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        
        {/* Header Overlay - Reduced padding */}
        <div className="absolute inset-0 p-2 sm:p-3 md:p-4 text-white flex flex-col justify-end">
          <div className="flex items-center justify-between">
            {/* Back button and business info */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <button
                onClick={() => navigate(-1)}
                className="p-1 sm:p-1.5 bg-black/20 backdrop-blur-sm rounded-lg hover:bg-black/30 transition-all"
              >
                <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </button>
              
              {/* Reduced logo size */}
              <div className="relative flex-shrink-0">
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg sm:rounded-xl bg-white shadow-neomorph flex items-center justify-center overflow-hidden">
                  {selectedBusiness.logoUrl ? (
                    <img
                      src={selectedBusiness.logoUrl}
                      alt={selectedBusiness.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Store className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
                  )}
                </div>
                {selectedBusiness.verified && (
                  <div className="absolute -bottom-0.5 -right-0.5 sm:-bottom-1 sm:-right-1 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-full p-0.5 shadow-lg">
                    <CheckCircle className="h-2 w-2 sm:h-2.5 sm:w-2.5" />
                  </div>
                )}
              </div>
              
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-0.5 sm:gap-1">
                  <h1 className="text-sm sm:text-xl md:text-2xl font-bold truncate">{selectedBusiness.name}</h1>
                  
                  {businessCategoryInfo && (
                    <span className={`inline-flex items-center px-1 sm:px-1.5 py-0.5 rounded-lg text-[6px] sm:text-[10px] font-bold shadow-md bg-white/20 backdrop-blur-sm border border-white/30`}>
                      <span className="mr-0.5">{businessCategoryInfo.icon}</span>
                      <span className="hidden xs:inline">{businessCategoryInfo.name}</span>
                    </span>
                  )}
                  
                  {selectedBusiness.featured && (
                    <span className="px-1 sm:px-1.5 py-0.5 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-[6px] sm:text-[10px] font-bold rounded-lg shadow-md">
                      FEATURED
                    </span>
                  )}
                  <span className={`px-1 sm:px-1.5 py-0.5 text-[6px] sm:text-[10px] font-bold rounded-lg shadow-md ${
                    selectedBusiness.tier === 'ENTERPRISE' ? 'bg-gradient-to-r from-green-500 to-green-600' :
                    selectedBusiness.tier === 'PREMIUM' ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
                    selectedBusiness.tier === 'FEATURED' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                    'bg-gradient-to-r from-gray-500 to-gray-600'
                  } text-white`}>
                    {selectedBusiness.tier}
                  </span>
                </div>
                
                <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-[8px] sm:text-xs text-white/90">
                  <div className="flex items-center">
                    {renderStars(selectedBusiness.rating || 0)}
                    <span className="ml-0.5 font-semibold text-[8px] sm:text-xs">
                      {selectedBusiness.rating?.toFixed(1) || '0.0'}
                    </span>
                    <span className="ml-0.5 text-white/70 text-[7px] sm:text-[10px]">
                      ({selectedBusiness.reviewCount || reviewsSummary?.totalReviews || 0})
                    </span>
                  </div>
                  
                  <div className="flex items-center min-w-0">
                    <MapPin className="h-2 w-2 sm:h-2.5 sm:w-2.5 mr-0.5 flex-shrink-0" />
                    <span className="truncate">{selectedBusiness.location}</span>
                  </div>
                  
                  {selectedBusiness.averageResponseTime && (
                    <div className="flex items-center hidden sm:flex">
                      <Clock className="h-2.5 w-2.5 mr-0.5" />
                      <span className="text-[8px] sm:text-xs">Avg. response: {selectedBusiness.averageResponseTime}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Action Buttons - Compact */}
            <div className="flex space-x-1 sm:space-x-2">
              {isOwner ? (
                <>
                  <button
                    onClick={() => setShowEditBusinessModal(true)}
                    className="px-1.5 sm:px-3 py-1 sm:py-1.5 bg-white/10 backdrop-blur-sm text-white font-medium rounded-lg border border-white/20 hover:bg-white/20 transition-all flex items-center text-[8px] sm:text-xs"
                  >
                    <Edit className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                    <span className="hidden xs:inline">Edit</span>
                  </button>
                  <button
                    onClick={() => navigate(`/marketplace/business/${businessId}/orders`)}
                    className="relative px-1.5 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-lg shadow-md shadow-orange-500/30 hover:shadow-lg transition-all flex items-center text-[8px] sm:text-xs"
                  >
                    <ShoppingBag className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                    <span className="hidden xs:inline">Orders</span>
                    {pendingOrdersCount > 0 && (
                      <span className="absolute -top-1 -right-1 px-1 py-0.5 bg-red-500 text-white text-[6px] sm:text-[8px] font-bold rounded-full shadow-md">
                        {pendingOrdersCount > 99 ? '99+' : pendingOrdersCount}
                      </span>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleSaveBusiness}
                    className={`p-1.5 sm:p-2 rounded-lg transition-all ${
                      isSaved 
                        ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md shadow-red-500/30' 
                        : 'bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 border border-white/20'
                    }`}
                    title={isSaved ? 'Remove from saved' : 'Save business'}
                  >
                    <Heart className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${isSaved ? 'fill-current' : ''}`} />
                  </button>
                  {selectedBusiness.canMessage && (
                    <button
                      onClick={() => setShowMessageModal(true)}
                      className="px-1.5 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all flex items-center text-[8px] sm:text-xs"
                    >
                      <MessageSquare className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                      <span className="hidden xs:inline">Message</span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs - Reduced padding and size */}
      <div className="bg-white rounded-xl shadow-neomorph p-0.5 sm:p-1 border border-primary-100/30 sticky top-14 z-10 mx-3 sm:mx-0">
        <nav className="flex flex-nowrap overflow-x-auto hide-scrollbar gap-0.5">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const count = tab.id === 'reviews' ? (selectedBusiness.reviewCount || reviewsSummary?.totalReviews) : null;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-1.5 sm:px-3 py-1.5 sm:py-2 rounded-lg font-medium text-[10px] sm:text-xs flex items-center transition-all whitespace-nowrap flex-shrink-0
                  ${activeTab === tab.id
                    ? 'bg-primary-50 text-primary-600 shadow-neomorph-inset border border-primary-100'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <div className={`p-0.5 rounded-md mr-0.5 sm:mr-1.5 ${activeTab === tab.id ? 'bg-primary-100' : 'bg-gray-100'}`}>
                  <Icon className={`h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 ${activeTab === tab.id ? 'text-primary-600' : 'text-gray-500'}`} />
                </div>
                <span className="hidden xs:inline">{tab.name}</span>
                <span className="xs:hidden">{tab.short}</span>
                {count > 0 && (
                  <span className="ml-0.5 sm:ml-1.5 bg-primary-100 text-primary-600 text-[6px] sm:text-[10px] rounded-full px-1 py-0.5">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content - Reduced padding */}
      <div className="pt-1 sm:pt-2 px-3 sm:px-0">
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-2 sm:space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-4">
              {/* Left Column */}
              <div className="lg:col-span-2 space-y-2 sm:space-y-4">
                {/* Shop Showcase - Reduced padding */}
                <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
                  <div className="flex items-center mb-2 sm:mb-3">
                    <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
                    <h3 className="text-xs sm:text-base font-semibold text-gray-800">
                      Shop Showcase
                    </h3>
                    {getAllItemsCount() > 0 && (
                      <span className="ml-1.5 text-[10px] sm:text-xs text-gray-500">({getAllItemsCount()} items)</span>
                    )}
                  </div>
                  
                  {getAllItemsCount() > 0 ? (
                    <>
                      <CategoryTabs
                        categories={getAvailableCategories()}
                        activeCategory={activeCategory}
                        onCategoryChange={setActiveCategory}
                        itemCounts={{
                          ALL: getAllItemsCount(),
                          ...Object.fromEntries(getAvailableCategories().map(c => [c.name, c.count]))
                        }}
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        sortBy={sortBy}
                        onSortChange={setSortBy}
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                      />
                      
                      {isFiltering && (
                        <div className="flex justify-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                        </div>
                      )}
                      
                      {!isFiltering && (
                        <>
                          {filteredItems.products.length > 0 && (
                            <div className={`${viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3' : 'space-y-2'}`}>
                              {filteredItems.products.map(product => (
                                <ProductServiceCard
                                  key={product.id}
                                  item={product}
                                  type="product"
                                  business={selectedBusiness}
                                  viewMode={viewMode}
                                  onQuickView={handleQuickView}
                                  onOrder={() => {
                                    setSelectedItemForOrder(product);
                                    setSelectedItemType('product');
                                    setShowOrderModal(true);
                                  }}
                                  onInquire={() => {
                                    setSelectedProduct(product);
                                    setShowMessageModal(true);
                                  }}
                                  onToggleFavorite={() => toggleFavorite(product.id)}
                                  isFavorited={false}
                                  isOwner={isOwner}
                                  onEdit={(item) => {
                                    setEditingProduct(item);
                                    setShowProductModal(true);
                                  }}
                                  onDelete={(productId) => handleDeleteProduct(productId)}
                                />
                              ))}
                            </div>
                          )}
                          
                          {filteredItems.services.length > 0 && (
                            <>
                              {filteredItems.products.length > 0 && (
                                <div className="mt-4 pt-3 border-t border-primary-100/50">
                                  <div className="flex items-center mb-2">
                                    <div className="w-1 h-3 bg-gradient-to-b from-purple-400 to-purple-600 rounded-full mr-1.5" />
                                    <h4 className="text-xs font-semibold text-gray-800">Services</h4>
                                  </div>
                                </div>
                              )}
                              <div className={`${viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3' : 'space-y-2'}`}>
                                {filteredItems.services.map(service => (
                                  <ProductServiceCard
                                    key={service.id}
                                    item={service}
                                    type="service"
                                    business={selectedBusiness}
                                    viewMode={viewMode}
                                    onQuickView={handleQuickView}
                                    onOrder={() => {
                                      setSelectedItemForOrder(service);
                                      setSelectedItemType('service');
                                      setShowOrderModal(true);
                                    }}
                                    onInquire={() => {
                                      setSelectedService(service);
                                      setShowMessageModal(true);
                                    }}
                                    onToggleFavorite={() => toggleFavorite(service.id)}
                                    isFavorited={false}
                                    isOwner={isOwner}
                                    onEdit={(item) => {
                                      setEditingService(item);
                                      setShowServiceModal(true);
                                    }}
                                    onDelete={(serviceId) => handleDeleteService(serviceId)}
                                  />
                                ))}
                              </div>
                            </>
                          )}
                          
                          {filteredItems.products.length === 0 && filteredItems.services.length === 0 && (
                            <div className="text-center py-6">
                              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 shadow-neomorph-inset mb-2">
                                <Package className="h-5 w-5 text-gray-400" />
                              </div>
                              <h4 className="text-xs font-medium text-gray-800 mb-0.5">No items found</h4>
                              <p className="text-[10px] text-gray-500">
                                {searchQuery 
                                  ? `No results for "${searchQuery}"`
                                  : `No ${activeCategory === 'ALL' ? 'items' : activeCategory} available`}
                              </p>
                              {(searchQuery || activeCategory !== 'ALL') && (
                                <button
                                  onClick={() => {
                                    setSearchQuery('');
                                    setActiveCategory('ALL');
                                  }}
                                  className="mt-1.5 text-[10px] text-primary-600 hover:text-primary-800"
                                >
                                  Clear filters
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-6">
                      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 shadow-neomorph-inset mb-2">
                        <Package className="h-5 w-5 text-gray-400" />
                      </div>
                      <h4 className="text-xs font-medium text-gray-800 mb-0.5">No products or services yet</h4>
                      <p className="text-[10px] text-gray-500">
                        {isOwner 
                          ? "You haven't listed any products or services yet."
                          : "This business hasn't listed any products or services yet."}
                      </p>
                      {isOwner && (
                        <button
                          onClick={() => {
                            setEditingProduct(null);
                            setShowProductModal(true);
                          }}
                          className="mt-2 px-3 py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-xs font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg"
                        >
                          Add Your First Product
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Description - Reduced padding */}
                <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
                  <div className="flex items-center mb-2 sm:mb-3">
                    <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
                    <h3 className="text-xs sm:text-base font-semibold text-gray-800">About {selectedBusiness.name}</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 whitespace-pre-line">
                    {selectedBusiness.description || 'No description provided.'}
                  </p>
                  {isOwner && !selectedBusiness.description && (
                    <button
                      onClick={() => navigate(`/marketplace/business/${businessId}/edit`)}
                      className="mt-2 px-3 py-1.5 bg-primary-50 text-primary-600 font-medium rounded-lg hover:bg-primary-100 transition-all flex items-center text-xs"
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Add Description
                    </button>
                  )}
                </div>

                {/* Contact Info - Reduced padding */}
                <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
                  <div className="flex items-center mb-2 sm:mb-3">
                    <div className="w-1 h-4 bg-gradient-to-b from-green-400 to-green-600 rounded-full mr-1.5 sm:mr-2" />
                    <h3 className="text-xs sm:text-base font-semibold text-gray-800">Contact Information</h3>
                  </div>
                  <div className="space-y-2">
                    {selectedBusiness.phone && (
                      <div className="flex items-center p-1.5 sm:p-2 bg-gray-50 rounded-lg">
                        <Phone className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 mr-1.5 sm:mr-2 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-[9px] sm:text-xs text-gray-500">Phone</p>
                          <p className="font-medium text-gray-800 text-xs sm:text-sm break-words">{selectedBusiness.phone}</p>
                        </div>
                      </div>
                    )}
                    
                    {selectedBusiness.email && (
                      <div className="flex items-center p-1.5 sm:p-2 bg-gray-50 rounded-lg">
                        <Mail className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 mr-1.5 sm:mr-2 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-[9px] sm:text-xs text-gray-500">Email</p>
                          <p className="font-medium text-gray-800 text-xs sm:text-sm break-words">{selectedBusiness.email}</p>
                        </div>
                      </div>
                    )}
                    
                    {selectedBusiness.website && (
                      <div className="flex items-center p-1.5 sm:p-2 bg-gray-50 rounded-lg">
                        <Globe className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 mr-1.5 sm:mr-2 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-[9px] sm:text-xs text-gray-500">Website</p>
                          <a
                            href={selectedBusiness.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-primary-600 hover:text-primary-800 text-xs sm:text-sm break-words"
                          >
                            {selectedBusiness.website}
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {selectedBusiness.address && (
                      <div className="flex items-center p-1.5 sm:p-2 bg-gray-50 rounded-lg">
                        <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 mr-1.5 sm:mr-2 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-[9px] sm:text-xs text-gray-500">Address</p>
                          <p className="font-medium text-gray-800 text-xs sm:text-sm break-words">{selectedBusiness.address}</p>
                        </div>
                      </div>
                    )}
                    
                    {isOwner && (
                      <button
                        onClick={() => navigate(`/marketplace/business/${businessId}/edit`)}
                        className="mt-1 px-3 py-1.5 bg-primary-50 text-primary-600 font-medium rounded-lg hover:bg-primary-100 transition-all flex items-center text-xs"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit Contact Info
                      </button>
                    )}
                  </div>
                </div>

                {/* Social Links */}
                {selectedBusiness.socialLinks && Object.keys(selectedBusiness.socialLinks).length > 0 && (
                  <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
                    <h3 className="text-xs sm:text-base font-semibold text-gray-800 mb-2 sm:mb-3">Follow on Social Media</h3>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {selectedBusiness.socialLinks.facebook && (
                        <a
                          href={selectedBusiness.socialLinks.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 sm:p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all shadow-neomorph-inset"
                        >
                          <Facebook className="h-3 w-3 sm:h-4 sm:w-4" />
                        </a>
                      )}
                      {selectedBusiness.socialLinks.twitter && (
                        <a
                          href={selectedBusiness.socialLinks.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 sm:p-2 bg-sky-50 text-sky-500 rounded-lg hover:bg-sky-100 transition-all shadow-neomorph-inset"
                        >
                          <Twitter className="h-3 w-3 sm:h-4 sm:w-4" />
                        </a>
                      )}
                      {selectedBusiness.socialLinks.instagram && (
                        <a
                          href={selectedBusiness.socialLinks.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 sm:p-2 bg-pink-50 text-pink-600 rounded-lg hover:bg-pink-100 transition-all shadow-neomorph-inset"
                        >
                          <Instagram className="h-3 w-3 sm:h-4 sm:w-4" />
                        </a>
                      )}
                      {selectedBusiness.socialLinks.linkedin && (
                        <a
                          href={selectedBusiness.socialLinks.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 sm:p-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-all shadow-neomorph-inset"
                        >
                          <Linkedin className="h-3 w-3 sm:h-4 sm:w-4" />
                        </a>
                      )}
                    </div>
                    {isOwner && (
                      <button
                        onClick={() => navigate(`/marketplace/business/${businessId}/edit`)}
                        className="mt-2 px-3 py-1.5 bg-primary-50 text-primary-600 font-medium rounded-lg hover:bg-primary-100 transition-all flex items-center text-xs"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit Social Links
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Right Column - Stats & Quick Actions - Reduced padding */}
              <div className="space-y-2 sm:space-y-4">
                {/* Business Stats */}
                <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <div className="flex items-center">
                      <div className="p-1 sm:p-1.5 bg-primary-50 rounded-lg mr-1.5 sm:mr-2">
                        <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 text-primary-600" />
                      </div>
                      <h3 className="text-xs sm:text-base font-semibold text-gray-800">Stats</h3>
                    </div>
                    {isOwner && (
                      <button
                        onClick={() => navigate(`/marketplace/business/${businessId}/analytics`)}
                        className="text-[9px] sm:text-xs text-primary-600 hover:text-primary-800 font-medium"
                      >
                        Analytics
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-2 sm:space-y-3">
                    <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                      <div className="p-1.5 sm:p-2 bg-gray-50 rounded-lg text-center">
                        <Package className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 mx-auto mb-0.5" />
                        <p className="text-[8px] sm:text-xs text-gray-500">Products</p>
                        <p className="text-xs sm:text-base font-semibold text-gray-800">
                          {selectedBusiness._count?.products || selectedBusiness.products?.length || 0}
                        </p>
                      </div>
                      
                      <div className="p-1.5 sm:p-2 bg-gray-50 rounded-lg text-center">
                        <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 mx-auto mb-0.5" />
                        <p className="text-[8px] sm:text-xs text-gray-500">Services</p>
                        <p className="text-xs sm:text-base font-semibold text-gray-800">
                          {selectedBusiness._count?.services || selectedBusiness.services?.length || 0}
                        </p>
                      </div>
                      
                      <div className="p-1.5 sm:p-2 bg-gray-50 rounded-lg text-center">
                        <Star className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 mx-auto mb-0.5" />
                        <p className="text-[8px] sm:text-xs text-gray-500">Reviews</p>
                        <p className="text-xs sm:text-base font-semibold text-gray-800">
                          {reviewsSummary?.totalReviews || selectedBusiness.reviewCount || 0}
                        </p>
                      </div>
                      
                      <div className="p-1.5 sm:p-2 bg-gray-50 rounded-lg text-center">
                        <ShoppingBag className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 mx-auto mb-0.5" />
                        <p className="text-[8px] sm:text-xs text-gray-500">Orders</p>
                        <p className="text-xs sm:text-base font-semibold text-gray-800">
                          {selectedBusiness._count?.orders || 0}
                        </p>
                      </div>
                    </div>
                    
                    {(selectedBusiness._count?.orders > 0 || selectedBusiness.stats?.totalOrders > 0) && (
                      <div className="pt-2 sm:pt-3 border-t border-primary-100/50">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[8px] sm:text-xs text-gray-600">Completion Rate</span>
                          <span className="text-[8px] sm:text-xs font-medium text-green-600">
                            {selectedBusiness.stats?.completionRate || 
                              (selectedBusiness._count?.reviews && selectedBusiness._count?.orders 
                                ? `${((selectedBusiness._count.reviews / selectedBusiness._count.orders) * 100).toFixed(1)}%` 
                                : '0%')}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1 shadow-neomorph-inset">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-green-600 h-1 rounded-full transition-all duration-300"
                            style={{ 
                              width: selectedBusiness.stats?.completionRate || 
                                (selectedBusiness._count?.reviews && selectedBusiness._count?.orders 
                                  ? `${((selectedBusiness._count.reviews / selectedBusiness._count.orders) * 100)}%` 
                                  : '0%')
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Business Tags */}
                {selectedBusiness.tags && selectedBusiness.tags.length > 0 && (
                  <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
                    <h3 className="text-xs sm:text-base font-semibold text-gray-800 mb-2 sm:mb-3">Business Tags</h3>
                    <div className="flex flex-wrap gap-1">
                      {selectedBusiness.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-1.5 sm:px-2 py-0.5 bg-primary-50 text-primary-700 text-[9px] sm:text-xs rounded-lg border border-primary-200"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    {isOwner && (
                      <button
                        onClick={() => navigate(`/marketplace/business/${businessId}/edit`)}
                        className="mt-2 px-3 py-1.5 bg-primary-50 text-primary-600 font-medium rounded-lg hover:bg-primary-100 transition-all flex items-center text-xs"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit Tags
                      </button>
                    )}
                  </div>
                )}

                {/* Gallery Preview */}
                {selectedBusiness.images && selectedBusiness.images.length > 0 && (
                  <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
                    <div className="flex items-center justify-between mb-2 sm:mb-3">
                      <h3 className="text-xs sm:text-base font-semibold text-gray-800">Gallery</h3>
                      {isOwner && (
                        <button
                          onClick={() => navigate(`/marketplace/business/${businessId}/gallery`)}
                          className="text-[9px] sm:text-xs text-primary-600 hover:text-primary-800 font-medium"
                        >
                          Manage
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-1 sm:gap-1.5">
                      {selectedBusiness.images.slice(0, 4).map((image, index) => (
                        <img
                          key={index}
                          src={image}
                          alt={`Gallery ${index + 1}`}
                          className="h-12 sm:h-14 w-full rounded-lg object-cover border border-gray-200"
                        />
                      ))}
                      {selectedBusiness.images.length > 4 && (
                        <div className="h-12 sm:h-14 w-full rounded-lg bg-gray-50 shadow-neomorph-inset flex items-center justify-center">
                          <span className="text-[8px] sm:text-xs text-gray-600 font-medium">
                            +{selectedBusiness.images.length - 4} more
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Quick Actions - Compact */}
                <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
                  <h3 className="text-xs sm:text-base font-semibold text-gray-800 mb-2 sm:mb-3">Quick Actions</h3>
                  <div className="space-y-1.5 sm:space-y-2">
                    {isOwner ? (
                      <>
                        <button
                          onClick={() => {
                            setEditingProduct(null);
                            setShowProductModal(true);
                          }}
                          className="w-full flex items-center justify-between p-1.5 sm:p-2 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all"
                        >
                          <div className="flex items-center min-w-0">
                            <div className="p-1 bg-blue-50 rounded-lg mr-1.5 sm:mr-2 flex-shrink-0">
                              <Package className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-blue-600" />
                            </div>
                            <div className="text-left min-w-0">
                              <p className="font-medium text-gray-800 text-xs sm:text-sm">Add Product</p>
                              <p className="text-[8px] sm:text-[10px] text-gray-500 truncate">List a new product</p>
                            </div>
                          </div>
                          <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0 ml-1" />
                        </button>
                        
                        <button
                          onClick={() => {
                            setEditingService(null);
                            setShowServiceModal(true);
                          }}
                          className="w-full flex items-center justify-between p-1.5 sm:p-2 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all"
                        >
                          <div className="flex items-center min-w-0">
                            <div className="p-1 bg-purple-50 rounded-lg mr-1.5 sm:mr-2 flex-shrink-0">
                              <TrendingUp className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-purple-600" />
                            </div>
                            <div className="text-left min-w-0">
                              <p className="font-medium text-gray-800 text-xs sm:text-sm">Add Service</p>
                              <p className="text-[8px] sm:text-[10px] text-gray-500 truncate">List a new service</p>
                            </div>
                          </div>
                          <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0 ml-1" />
                        </button>
                        
                        <button
                          onClick={() => navigate(`/marketplace/business/${businessId}/orders`)}
                          className="w-full flex items-center justify-between p-1.5 sm:p-2 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all"
                        >
                          <div className="flex items-center min-w-0">
                            <div className="p-1 bg-green-50 rounded-lg mr-1.5 sm:mr-2 flex-shrink-0">
                              <ShoppingBag className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-green-600" />
                            </div>
                            <div className="text-left min-w-0">
                              <p className="font-medium text-gray-800 text-xs sm:text-sm">View Orders</p>
                              <p className="text-[8px] sm:text-[10px] text-gray-500 truncate">Manage incoming orders</p>
                            </div>
                          </div>
                          <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0 ml-1" />
                        </button>
                      </>
                    ) : (
                      <>
                        {selectedBusiness.canMessage && (
                          <button
                            onClick={() => setShowMessageModal(true)}
                            className="w-full flex items-center justify-between p-1.5 sm:p-2 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all"
                          >
                            <div className="flex items-center min-w-0">
                              <div className="p-1 bg-primary-50 rounded-lg mr-1.5 sm:mr-2 flex-shrink-0">
                                <MessageSquare className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-primary-600" />
                              </div>
                              <div className="text-left min-w-0">
                                <p className="font-medium text-gray-800 text-xs sm:text-sm">Message</p>
                                <p className="text-[8px] sm:text-[10px] text-gray-500 truncate">Ask questions or request quotes</p>
                              </div>
                            </div>
                            <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0 ml-1" />
                          </button>
                        )}
                        <button
                          onClick={handleSaveBusiness}
                          className="w-full flex items-center justify-between p-1.5 sm:p-2 bg-gray-50 rounded-lg hover:bg-white hover:shadow-neomorph-hover transition-all"
                        >
                          <div className="flex items-center min-w-0">
                            <div className={`p-1 rounded-lg mr-1.5 sm:mr-2 flex-shrink-0 ${isSaved ? 'bg-red-50' : 'bg-gray-100'}`}>
                              <Heart className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${isSaved ? 'text-red-500 fill-current' : 'text-gray-400'}`} />
                            </div>
                            <div className="text-left min-w-0">
                              <p className="font-medium text-gray-800 text-xs sm:text-sm">
                                {isSaved ? 'Remove' : 'Save Business'}
                              </p>
                              <p className="text-[8px] sm:text-[10px] text-gray-500 truncate">
                                {isSaved ? 'Remove from favorites' : 'Add to favorites'}
                              </p>
                            </div>
                          </div>
                          <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0 ml-1" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PRODUCTS TAB - Compact */}
        {activeTab === 'products' && (
          <div className="space-y-2 sm:space-y-4">
            <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3 sm:mb-4">
                <div>
                  <h3 className="text-sm sm:text-lg font-bold text-gray-800">Products</h3>
                  <p className="text-[10px] sm:text-sm text-gray-500">
                    {selectedBusiness._count?.products || selectedBusiness.products?.length || 0} products available
                  </p>
                </div>
                <div className="flex space-x-1.5 sm:space-x-2">
                  {isOwner && (
                    <>
                      <button
                        onClick={() => {
                          setEditingProduct(null);
                          setShowProductModal(true);
                        }}
                        className="px-2 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all flex items-center text-[10px] sm:text-xs"
                      >
                        <Package className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                        Add
                      </button>
                      <button
                        onClick={() => navigate(`/marketplace/business/${businessId}/products/manage`)}
                        className="px-2 sm:px-3 py-1 sm:py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center text-[10px] sm:text-xs"
                      >
                        <Edit className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                        Manage
                      </button>
                    </>
                  )}
                </div>
              </div>

              {selectedBusiness.products && selectedBusiness.products.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                  {selectedBusiness.products.map((product) => {
                    const productCategoryInfo = getProductCategoryInfo(product.category);
                    return (
                      <div
                        key={product.id}
                        className="bg-white rounded-xl shadow-neomorph overflow-hidden hover:shadow-neomorph-hover transition-all border border-primary-100/30 relative"
                      >
                        {isOwner && (
                          <div className="absolute top-1 right-1 sm:top-2 sm:right-2 z-10 flex space-x-0.5">
                            <button
                              onClick={() => {
                                setEditingProduct(product);
                                setShowProductModal(true);
                              }}
                              className="p-0.5 sm:p-1 bg-white rounded-lg shadow-md hover:shadow-lg transition-all"
                              title="Edit product"
                            >
                              <Edit className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-gray-600" />
                            </button>
                          </div>
                        )}
                        {product.images && product.images.length > 0 ? (
                          <div className="h-24 sm:h-32 relative">
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                            {product.featured && (
                              <div className="absolute top-1 left-1 sm:top-2 sm:left-2 px-1 sm:px-1.5 py-0.5 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-[6px] sm:text-[10px] font-bold rounded-lg shadow-md">
                                FEATURED
                              </div>
                            )}
                            <div className="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 px-1 sm:px-1.5 py-0.5 bg-black/50 backdrop-blur-sm text-white text-[6px] sm:text-[10px] rounded-lg">
                              {productCategoryInfo.icon} {productCategoryInfo.name}
                            </div>
                          </div>
                        ) : (
                          <div className="h-24 sm:h-32 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                            <Package className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                            <div className="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 px-1 sm:px-1.5 py-0.5 bg-black/50 backdrop-blur-sm text-white text-[6px] sm:text-[10px] rounded-lg">
                              {productCategoryInfo.icon} {productCategoryInfo.name}
                            </div>
                          </div>
                        )}
                        
                        <div className="p-2 sm:p-3">
                          <h4 className="font-bold text-gray-800 text-xs sm:text-sm mb-0.5 truncate">{product.name}</h4>
                          <p className="text-gray-600 text-[9px] sm:text-xs mb-1 line-clamp-2">
                            {product.description}
                          </p>
                          
                          <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                            <div>
                              <span className="text-xs sm:text-sm font-bold text-gray-800">
                                ₦{product.price?.toLocaleString()}
                              </span>
                              {product.compareAtPrice && (
                                <span className="ml-1 text-[8px] sm:text-[10px] text-gray-500 line-through">
                                  ₦{product.compareAtPrice.toLocaleString()}
                                </span>
                              )}
                            </div>
                            
                            {product._count?.reviews > 0 && (
                              <div className="flex items-center">
                                {renderStars(product.rating || 0)}
                                <span className="ml-0.5 text-[8px] sm:text-[10px] text-gray-500">
                                  ({product._count.reviews})
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex space-x-1.5">
                            {isOwner ? (
                              <button
                                onClick={() => handleDeleteProduct(product.id)}
                                className="flex-1 px-1.5 sm:px-2 py-1 bg-red-50 text-red-600 font-medium rounded-lg hover:bg-red-100 transition-all text-[8px] sm:text-[10px]"
                              >
                                Delete
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    setSelectedItemForOrder(product);
                                    setSelectedItemType('product');
                                    setShowOrderModal(true);
                                  }}
                                  className="flex-1 px-1.5 sm:px-2 py-1 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-[8px] sm:text-[10px] flex items-center justify-center"
                                >
                                  <ShoppingBag className="h-2.5 w-2.5 mr-0.5" />
                                  Buy
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedProduct(product);
                                    setShowMessageModal(true);
                                  }}
                                  className="flex-1 px-1.5 sm:px-2 py-1 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-[8px] sm:text-[10px]"
                                >
                                  Inquire
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8">
                  <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-blue-50 shadow-neomorph-inset mb-2 sm:mb-3">
                    <Package className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
                  </div>
                  <h4 className="text-sm sm:text-base font-medium text-gray-800 mb-0.5 sm:mb-1">No Products Listed</h4>
                  <p className="text-[10px] sm:text-sm text-gray-500 mb-3 sm:mb-4">
                    {isOwner ? "Start selling today!" : "This business hasn't listed any products yet."}
                  </p>
                  {isOwner && (
                    <button
                      onClick={() => {
                        setEditingProduct(null);
                        setShowProductModal(true);
                      }}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-xs"
                    >
                      Add Your First Product
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* SERVICES TAB - Compact */}
        {activeTab === 'services' && (
          <div className="space-y-2 sm:space-y-4">
            <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3 sm:mb-4">
                <div>
                  <h3 className="text-sm sm:text-lg font-bold text-gray-800">Services</h3>
                  <p className="text-[10px] sm:text-sm text-gray-500">
                    {selectedBusiness._count?.services || selectedBusiness.services?.length || 0} services available
                  </p>
                </div>
                <div className="flex space-x-1.5 sm:space-x-2">
                  {isOwner && (
                    <>
                      <button
                        onClick={() => {
                          setEditingService(null);
                          setShowServiceModal(true);
                        }}
                        className="px-2 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all flex items-center text-[10px] sm:text-xs"
                      >
                        <TrendingUp className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                        Add
                      </button>
                      <button
                        onClick={() => navigate(`/marketplace/business/${businessId}/services/manage`)}
                        className="px-2 sm:px-3 py-1 sm:py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center text-[10px] sm:text-xs"
                      >
                        <Edit className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1" />
                        Manage
                      </button>
                    </>
                  )}
                </div>
              </div>

              {selectedBusiness.services && selectedBusiness.services.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                  {selectedBusiness.services.map((service) => {
                    const serviceCategoryInfo = getProductCategoryInfo(service.category);
                    return (
                      <div
                        key={service.id}
                        className="bg-white rounded-xl shadow-neomorph overflow-hidden hover:shadow-neomorph-hover transition-all border border-primary-100/30 relative"
                      >
                        {isOwner && (
                          <div className="absolute top-1 right-1 sm:top-2 sm:right-2 z-10 flex space-x-0.5">
                            <button
                              onClick={() => {
                                setEditingService(service);
                                setShowServiceModal(true);
                              }}
                              className="p-0.5 sm:p-1 bg-white rounded-lg shadow-md hover:shadow-lg transition-all"
                              title="Edit service"
                            >
                              <Edit className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-gray-600" />
                            </button>
                          </div>
                        )}
                        {service.images && service.images.length > 0 ? (
                          <div className="h-24 sm:h-32 relative">
                            <img
                              src={service.images[0]}
                              alt={service.title}
                              className="w-full h-full object-cover"
                            />
                            {service.featured && (
                              <div className="absolute top-1 left-1 sm:top-2 sm:left-2 px-1 sm:px-1.5 py-0.5 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-[6px] sm:text-[10px] font-bold rounded-lg shadow-md">
                                FEATURED
                              </div>
                            )}
                            <div className="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 px-1 sm:px-1.5 py-0.5 bg-black/50 backdrop-blur-sm text-white text-[6px] sm:text-[10px] rounded-lg">
                              {serviceCategoryInfo.icon} {serviceCategoryInfo.name}
                            </div>
                          </div>
                        ) : (
                          <div className="h-24 sm:h-32 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                            <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                            <div className="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 px-1 sm:px-1.5 py-0.5 bg-black/50 backdrop-blur-sm text-white text-[6px] sm:text-[10px] rounded-lg">
                              {serviceCategoryInfo.icon} {serviceCategoryInfo.name}
                            </div>
                          </div>
                        )}
                        
                        <div className="p-2 sm:p-3">
                          <h4 className="font-bold text-gray-800 text-xs sm:text-sm mb-0.5 truncate">{service.title}</h4>
                          <p className="text-gray-600 text-[9px] sm:text-xs mb-1 line-clamp-2">
                            {service.description}
                          </p>
                          
                          <div className="space-y-0.5 sm:space-y-1 mb-1.5 sm:mb-2">
                            <div className="flex justify-between">
                              <span className="text-[8px] sm:text-[10px] text-gray-500">Starting from:</span>
                              <span className="text-xs sm:text-sm font-bold text-gray-800">
                                ₦{service.basePrice?.toLocaleString()}
                              </span>
                            </div>
                            
                            {service.deliveryTime && (
                              <div className="flex justify-between">
                                <span className="text-[8px] sm:text-[10px] text-gray-500">Delivery:</span>
                                <span className="text-[8px] sm:text-[10px] text-gray-700">{service.deliveryTime}</span>
                              </div>
                            )}
                            
                            <div className="flex justify-between">
                              <span className="text-[8px] sm:text-[10px] text-gray-500">Completed:</span>
                              <span className="text-[8px] sm:text-[10px] text-gray-700">{service.completedOrders} orders</span>
                            </div>
                          </div>
                          
                          <div className="flex space-x-1.5">
                            {isOwner ? (
                              <button
                                onClick={() => handleDeleteService(service.id)}
                                className="flex-1 px-1.5 sm:px-2 py-1 bg-red-50 text-red-600 font-medium rounded-lg hover:bg-red-100 transition-all text-[8px] sm:text-[10px]"
                              >
                                Delete
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    setSelectedItemForOrder(service);
                                    setSelectedItemType('service');
                                    setShowOrderModal(true);
                                  }}
                                  className="flex-1 px-1.5 sm:px-2 py-1 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-[8px] sm:text-[10px] flex items-center justify-center"
                                >
                                  <ShoppingBag className="h-2.5 w-2.5 mr-0.5" />
                                  Book
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedService(service);
                                    setShowMessageModal(true);
                                  }}
                                  className="flex-1 px-1.5 sm:px-2 py-1 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-[8px] sm:text-[10px]"
                                >
                                  Inquire
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8">
                  <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-purple-50 shadow-neomorph-inset mb-2 sm:mb-3">
                    <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-purple-400" />
                  </div>
                  <h4 className="text-sm sm:text-base font-medium text-gray-800 mb-0.5 sm:mb-1">No Services Listed</h4>
                  <p className="text-[10px] sm:text-sm text-gray-500 mb-3 sm:mb-4">
                    {isOwner ? "Start offering your services!" : "This business hasn't listed any services yet."}
                  </p>
                  {isOwner && (
                    <button
                      onClick={() => {
                        setEditingService(null);
                        setShowServiceModal(true);
                      }}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-xs"
                    >
                      Add Your First Service
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* REVIEWS TAB - Compact */}
        {activeTab === 'reviews' && (
          <div className="space-y-2 sm:space-y-4">
            {/* Reviews Summary */}
            <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                {/* Average Rating */}
                <div className="sm:w-1/3 text-center">
                  <div className="text-2xl sm:text-4xl font-bold text-gray-800 mb-0.5 sm:mb-1">
                    {reviewsSummary?.averageRating?.toFixed(1) || selectedBusiness.rating?.toFixed(1) || '0.0'}
                  </div>
                  <div className="flex justify-center mb-0.5 sm:mb-1">
                    {renderStars(reviewsSummary?.averageRating || selectedBusiness.rating || 0)}
                  </div>
                  <p className="text-[9px] sm:text-xs text-gray-500 mb-2 sm:mb-3">
                    {reviewsSummary?.totalReviews || selectedBusiness.reviewCount || 0} reviews
                  </p>
                  {!isOwner && (
                    <button
                      onClick={() => setShowReviewModal(true)}
                      className="px-2.5 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-[10px] sm:text-xs"
                    >
                      Write a Review
                    </button>
                  )}
                </div>

                {/* Rating Distribution */}
                <div className="sm:w-2/3">
                  <h4 className="font-semibold text-gray-800 text-xs sm:text-sm mb-1.5 sm:mb-2">Rating Breakdown</h4>
                  <div className="space-y-1.5 sm:space-y-2">
                    {[5, 4, 3, 2, 1].map((star) => {
                      const count = reviewsSummary?.ratingDistribution?.[star] || 0;
                      const percentage = getRatingPercentage(count);
                      return (
                        <div key={star} className="flex items-center gap-1 sm:gap-2">
                          <div className="text-[9px] sm:text-xs text-gray-600 w-8">{star} star</div>
                          <div className="flex-1">
                            <div className="w-full bg-gray-200 rounded-full h-1 sm:h-1.5 shadow-neomorph-inset">
                              <div
                                className="bg-gradient-to-r from-yellow-400 to-yellow-500 h-1 sm:h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className="text-[9px] sm:text-xs text-gray-600 w-6 text-right">
                            {count}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Reviews Filter */}
            <div className="bg-white rounded-xl shadow-neomorph p-2 sm:p-3 border border-primary-100/30 overflow-x-auto">
              <div className="flex items-center justify-between min-w-max sm:min-w-0">
                <div className="flex items-center space-x-1">
                  <div className="p-0.5 sm:p-1 bg-gray-100 rounded-lg">
                    <Filter className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-gray-500" />
                  </div>
                  <span className="font-medium text-gray-700 text-[9px] sm:text-xs">Filter:</span>
                </div>
                <div className="flex flex-wrap gap-0.5 sm:gap-1">
                  <button
                    onClick={() => setReviewsFilter('all')}
                    className={`px-1.5 sm:px-2 py-0.5 rounded-lg text-[8px] sm:text-[10px] font-medium transition-all ${
                      reviewsFilter === 'all'
                        ? 'bg-primary-100 text-primary-700 shadow-neomorph-inset border border-primary-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    All
                  </button>
                  {[5, 4, 3, 2, 1].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => setReviewsFilter(rating.toString())}
                      className={`px-1 sm:px-2 py-0.5 rounded-lg text-[8px] sm:text-[10px] font-medium flex items-center transition-all ${
                        reviewsFilter === rating.toString()
                          ? 'bg-yellow-100 text-yellow-700 shadow-neomorph-inset border border-yellow-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Star className="h-2 w-2 sm:h-2.5 sm:w-2.5 mr-0.5" />
                      {rating}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Reviews List */}
            <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
              {filteredReviews && filteredReviews.length > 0 ? (
                <div className="space-y-3 sm:space-y-4">
                  {filteredReviews.map((review) => (
                    <div key={review.id} className="border-b border-primary-100/50 pb-3 sm:pb-4 last:border-0 last:pb-0">
                      <div className="flex items-start justify-between mb-1.5 sm:mb-2">
                        <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0 flex-1">
                          <img
                            src={review.reviewer.avatar}
                            alt={review.reviewer.firstName}
                            className="h-6 w-6 sm:h-8 sm:w-8 rounded-lg object-cover border border-white shadow-md flex-shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <h4 className="font-medium text-gray-800 text-[10px] sm:text-xs truncate">
                              {review.reviewer.firstName} {review.reviewer.lastName}
                            </h4>
                            <div className="flex flex-wrap items-center gap-0.5 sm:gap-1 text-[8px] sm:text-xs text-gray-500">
                              <div className="flex items-center">
                                {renderStars(review.rating)}
                              </div>
                              <span className="hidden xs:inline">•</span>
                              <span className="text-[7px] sm:text-[10px]">
                                {new Date(review.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {review.title && (
                          <div className="text-right flex-shrink-0 ml-1">
                            <h5 className="font-medium text-gray-800 text-[10px] sm:text-xs truncate max-w-[100px] sm:max-w-none">{review.title}</h5>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-gray-600 text-[10px] sm:text-xs mb-2 sm:mb-3">{review.comment}</p>
                      
                      {review.images && review.images.length > 0 && (
                        <div className="flex space-x-1 sm:space-x-1.5 mb-2 sm:mb-3">
                          {review.images.slice(0, 3).map((image, index) => (
                            <img
                              key={index}
                              src={image}
                              alt={`Review ${index + 1}`}
                              className="h-10 w-10 sm:h-14 sm:w-14 rounded-lg object-cover border border-gray-200"
                            />
                          ))}
                          {review.images.length > 3 && (
                            <div className="h-10 w-10 sm:h-14 sm:w-14 rounded-lg bg-gray-50 shadow-neomorph-inset flex items-center justify-center">
                              <span className="text-[8px] sm:text-[10px] text-gray-600">+{review.images.length - 3}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {review.sellerResponse ? (
                        <div className="bg-gray-50 rounded-lg p-2 sm:p-3 border-l-4 border-primary-500 shadow-neomorph-inset">
                          <div className="flex items-start gap-1.5 sm:gap-2">
                            <div className="flex-shrink-0">
                              <img
                                src={selectedBusiness.logoUrl}
                                alt={selectedBusiness.name}
                                className="h-5 w-5 sm:h-6 sm:w-6 rounded-lg"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center justify-between gap-0.5">
                                <p className="text-[8px] sm:text-xs font-medium text-gray-800">
                                  Response from {selectedBusiness.name}
                                </p>
                                {isOwner && (
                                  <button
                                    onClick={() => toast.success('Edit response coming soon!')}
                                    className="p-0.5 text-gray-400 hover:text-gray-600"
                                  >
                                    <Edit className="h-2 w-2 sm:h-2.5 sm:w-2.5" />
                                  </button>
                                )}
                              </div>
                              <p className="mt-0.5 text-[8px] sm:text-xs text-gray-600">{review.sellerResponse}</p>
                              <p className="mt-0.5 text-[7px] sm:text-[10px] text-gray-500">
                                {new Date(review.respondedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        isOwner && (
                          <button
                            onClick={() => toast.success('Respond to review coming soon!')}
                            className="mt-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 bg-primary-50 text-primary-600 font-medium rounded-lg hover:bg-primary-100 transition-all text-[8px] sm:text-[10px]"
                          >
                            Respond
                          </button>
                        )
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8">
                  <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-yellow-50 shadow-neomorph-inset mb-2 sm:mb-3">
                    <Star className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400" />
                  </div>
                  <h4 className="text-sm sm:text-base font-medium text-gray-800 mb-0.5 sm:mb-1">No Reviews Yet</h4>
                  <p className="text-[10px] sm:text-sm text-gray-500 mb-3 sm:mb-4">
                    {reviewsFilter !== 'all'
                      ? `No ${reviewsFilter}-star reviews found`
                      : isOwner
                      ? "You haven't received any reviews yet."
                      : 'Be the first to review this business!'}
                  </p>
                  {!isOwner && (
                    <button
                      onClick={() => setShowReviewModal(true)}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-xs"
                    >
                      Write First Review
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ABOUT TAB - Compact */}
        {activeTab === 'about' && (
          <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-4 border border-primary-100/30">
            <div className="space-y-3 sm:space-y-4">
              {/* Business Owner */}
              <div>
                <div className="flex items-center mb-2 sm:mb-3">
                  <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
                  <h3 className="text-xs sm:text-base font-semibold text-gray-800">Business Owner</h3>
                </div>
                <div className="flex items-center gap-2 p-2 sm:p-3 bg-gray-50 rounded-lg shadow-neomorph-inset">
                  <img
                    src={selectedBusiness.user.avatar}
                    alt={selectedBusiness.user.firstName}
                    className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg object-cover border border-white shadow-md flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-gray-800 text-xs sm:text-sm truncate">
                      {selectedBusiness.user.firstName} {selectedBusiness.user.lastName}
                    </h4>
                    <p className="text-gray-600 text-[10px] sm:text-xs mb-0.5 sm:mb-1 break-words">{selectedBusiness.user.bio}</p>
                    <div className="flex flex-wrap gap-0.5 sm:gap-1">
                      {selectedBusiness.user.businessTags?.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="px-1 sm:px-1.5 py-0.5 bg-primary-50 text-primary-700 text-[8px] sm:text-[10px] rounded-lg"
                        >
                          {tag}
                        </span>
                      ))}
                      {selectedBusiness.user.businessTags?.length > 3 && (
                        <span className="px-1 sm:px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[8px] sm:text-[10px] rounded-lg">
                          +{selectedBusiness.user.businessTags.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Verification */}
              <div>
                <div className="flex items-center mb-2 sm:mb-3">
                  <div className="w-1 h-4 bg-gradient-to-b from-green-400 to-green-600 rounded-full mr-1.5 sm:mr-2" />
                  <h3 className="text-xs sm:text-base font-semibold text-gray-800">Verification Status</h3>
                </div>
                <div className="space-y-1.5 sm:space-y-2">
                  <div className="flex items-center justify-between p-1.5 sm:p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center min-w-0">
                      <div className="p-0.5 sm:p-1 bg-green-100 rounded-lg mr-1.5 sm:mr-2 flex-shrink-0">
                        <CheckCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-green-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 text-[10px] sm:text-xs">Business Verification</p>
                        <p className="text-[8px] sm:text-[10px] text-gray-500 truncate">Verified business details</p>
                      </div>
                    </div>
                    <span className="text-[8px] sm:text-xs font-medium text-green-600 flex-shrink-0 pl-1">
                      {selectedBusiness.verified ? 'Verified' : 'Pending'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-1.5 sm:p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center min-w-0">
                      <div className="p-0.5 sm:p-1 bg-blue-100 rounded-lg mr-1.5 sm:mr-2 flex-shrink-0">
                        <CheckCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 text-[10px] sm:text-xs">Phone Verification</p>
                        <p className="text-[8px] sm:text-[10px] text-gray-500 truncate">Verified phone number</p>
                      </div>
                    </div>
                    <span className="text-[8px] sm:text-xs font-medium text-blue-600 flex-shrink-0 pl-1">
                      {selectedBusiness.user.phoneVerified ? 'Verified' : 'Pending'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-1.5 sm:p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center min-w-0">
                      <div className="p-0.5 sm:p-1 bg-purple-100 rounded-lg mr-1.5 sm:mr-2 flex-shrink-0">
                        <Award className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-purple-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 text-[10px] sm:text-xs">Seller Tier</p>
                        <p className="text-[8px] sm:text-[10px] text-gray-500 truncate">Based on VTU purchases</p>
                      </div>
                    </div>
                    <span className={`text-[8px] sm:text-xs font-medium px-1 sm:px-1.5 py-0.5 rounded-lg flex-shrink-0 ml-1 ${
                      selectedBusiness.tier === 'ENTERPRISE' ? 'bg-green-100 text-green-700' :
                      selectedBusiness.tier === 'PREMIUM' ? 'bg-purple-100 text-purple-700' :
                      selectedBusiness.tier === 'FEATURED' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {selectedBusiness.tier}
                    </span>
                  </div>
                </div>
              </div>

              {/* Gallery */}
              {selectedBusiness.images && selectedBusiness.images.length > 0 && (
                <div>
                  <div className="flex items-center mb-2 sm:mb-3">
                    <div className="w-1 h-4 bg-gradient-to-b from-pink-400 to-pink-600 rounded-full mr-1.5 sm:mr-2" />
                    <h3 className="text-xs sm:text-base font-semibold text-gray-800">Business Gallery</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 sm:gap-2">
                    {selectedBusiness.images.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Gallery ${index + 1}`}
                        className="h-16 sm:h-24 w-full rounded-lg object-cover border border-gray-200 shadow-neomorph-inset"
                      />
                    ))}
                  </div>
                  {isOwner && (
                    <button
                      onClick={() => navigate(`/marketplace/business/${businessId}/gallery`)}
                      className="mt-2 px-3 py-1.5 bg-primary-50 text-primary-600 font-medium rounded-lg hover:bg-primary-100 transition-all flex items-center text-xs"
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Manage Gallery
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showMessageModal && (
        <MessageBusinessModal
          business={selectedBusiness}
          product={selectedProduct}
          service={selectedService}
          onClose={() => {
            setShowMessageModal(false);
            setSelectedProduct(null);
            setSelectedService(null);
          }}
          onSuccess={() => toast.success('Message sent successfully!')}
        />
      )}

      {showReviewModal && (
        <ReviewModal
          business={selectedBusiness}
          product={selectedProduct}
          service={selectedService}
          onClose={() => setShowReviewModal(false)}
          onSuccess={() => {
            Promise.all([
              getBusinessDetails(businessId),
              getReviews({ businessId })
            ]).then(() => {
              toast.success('Review submitted successfully!');
            });
          }}
        />
      )}

      {showOrderModal && selectedItemForOrder && (
        <OrderModal
          item={selectedItemForOrder}
          type={selectedItemType}
          business={selectedBusiness}
          onClose={() => {
            setShowOrderModal(false);
            setSelectedItemForOrder(null);
            setSelectedItemType(null);
          }}
          onSuccess={(order) => {
            toast.success('Order placed successfully!');
          }}
        />
      )}

      <ProductModal
        isOpen={showProductModal}
        businessId={businessId}
        product={editingProduct}
        onClose={() => {
          setShowProductModal(false);
          setEditingProduct(null);
        }}
        onSuccess={() => {
          loadBusinessData();
          toast.success(editingProduct ? 'Product updated successfully!' : 'Product added successfully!');
        }}
      />

      <ServiceModal
        isOpen={showServiceModal}
        businessId={businessId}
        service={editingService}
        onClose={() => {
          setShowServiceModal(false);
          setEditingService(null);
        }}
        onSuccess={() => {
          loadBusinessData();
          toast.success(editingService ? 'Service updated successfully!' : 'Service added successfully!');
        }}
      />

      {showEditBusinessModal && (
        <EditBusinessModal
          business={selectedBusiness}
          onClose={() => setShowEditBusinessModal(false)}
          onSuccess={() => {
            loadBusinessData();
            toast.success('Business updated successfully!');
          }}
        />
      )}

      {quickViewItem && (
        <QuickViewModal
          item={quickViewItem}
          type={quickViewType}
          business={selectedBusiness}
          onClose={() => {
            setQuickViewItem(null);
            setQuickViewType(null);
          }}
          onOrder={handleOrderFromQuickView}
          onInquire={handleInquireFromQuickView}
          onToggleFavorite={() => toggleFavorite(quickViewItem.id)}
          isFavorited={false}
        />
      )}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default BusinessProfilePage;